/**
 * Lease Price Calculator for Bike Configurator
 * Calculates monthly lease prices based on bike price, lease type, and term.
 * Supports compare_at_price for "van/voor" (was/now) display.
 *
 * Security: Input validation, no innerHTML, sanitized output.
 */

(function() {
  'use strict';

  const DEBUG = false;

  // --- Actie: eerste X maanden Y% korting (alleen visueel) ---
  // LET OP: deze waarden moeten gelijk blijven aan die in init-lease-pricing.liquid (kaarten).
  const PROMO_DISCOUNT_RATE = 0.5; // 50%
  const PROMO_MONTHS = 3;
  // Tekst in de gele actie-badge. 'WK-actie:' is de campagnenaam (vrij aan te passen);
  // het percentage wordt uit PROMO_DISCOUNT_RATE afgeleid zodat het altijd klopt.
  const PROMO_BADGE_LABEL = 'WK-actie: ' + Math.round(PROMO_DISCOUNT_RATE * 100) + '% korting';

  // Helper: bereken gehalveerde (actie)prijs, afgerond op centen.
  function promoPriceFrom(normalPrice) {
    return Math.round(normalPrice * (1 - PROMO_DISCOUNT_RATE) * 100) / 100;
  }

  function debugLog(...args) {
    if (DEBUG) console.log('[LeaseCalculator]', ...args);
  }

  class LeaseCalculator extends HTMLElement {
    constructor() {
      super();
      this.leaseType = 'prive';
      this.leaseTerm = '48';
      this.basePrice = 0;
      this.compareAtPrice = 0; // 0 = no compare price / no discount
      this.leaseTableData = [];

      // Store both prices for comparison display
      this.privePrice = null;
      this.zakelijkPrice = null;
    }

    connectedCallback() {
      this.loadLeaseTableData();
      this.initializeCalculator();
    }

    /**
     * Load and validate lease table data from JSON script tag
     */
    loadLeaseTableData() {
      var leaseTableScript = document.getElementById('lease-table-data');
      if (!leaseTableScript) {
        debugLog('Lease table data not found. Make sure pricing_table metaobject is configured.');
        return;
      }

      try {
        var rawData = JSON.parse(leaseTableScript.textContent);
        this.leaseTableData = this.validateLeaseTableData(rawData);
        debugLog('Lease prices loaded:', this.leaseTableData.length, 'entries');
      } catch (e) {
        debugLog('Failed to parse lease table data:', e);
        this.leaseTableData = [];
      }
    }

    /**
     * Validate a number is finite and within reasonable bounds
     */
    validateNumber(value, min, max) {
      if (min === undefined) min = 0;
      if (max === undefined) max = 1000000;

      var num = parseFloat(value);
      if (typeof num !== 'number' || !isFinite(num) || isNaN(num)) {
        return null;
      }
      if (num < min || num > max) {
        return null;
      }
      return num;
    }

    /**
     * Validate lease table data structure and values
     */
    validateLeaseTableData(data) {
      var self = this;

      if (!Array.isArray(data)) {
        debugLog('Lease table data is not an array');
        return [];
      }

      // Limit array size to prevent DoS
      var maxEntries = 1000;
      if (data.length > maxEntries) {
        debugLog('Lease table data exceeds maximum entries, truncating');
        data = data.slice(0, maxEntries);
      }

      return data.filter(function(entry) {
        if (!entry || typeof entry !== 'object') {
          return false;
        }

        if (entry.type !== 'prive' && entry.type !== 'zakelijk') {
          debugLog('Invalid lease type:', entry.type);
          return false;
        }

        var van = self.validateNumber(entry.van, 0, 100000);
        var totEnMet = self.validateNumber(entry.tot_en_met, 0, 100000);
        var leasePrice36 = self.validateNumber(entry.lease_price_36, 0, 10000);

        if (van === null || totEnMet === null || leasePrice36 === null) {
          debugLog('Invalid numeric values in entry:', entry);
          return false;
        }

        if (totEnMet < van) {
          debugLog('Invalid range: tot_en_met < van:', entry);
          return false;
        }

        if (entry.type === 'prive') {
          var leasePrice48 = self.validateNumber(entry.lease_price_48, 0, 10000);
          if (leasePrice48 === null) {
            debugLog('Invalid lease_price_48 for prive entry:', entry);
            return false;
          }
        }

        return true;
      });
    }

    initializeCalculator() {
      this.loadInitialPrice();

      // Check price threshold for zakelijk-only mode (based on current price, not compare-at)
      if (this.basePrice >= 5000) {
        this.enforceZakelijkOnly();
      }

      this.setupEventListeners();
      this.updateLeasePrice();
    }

    /**
     * Load initial prices from selected Accu option or product base price element.
     * Populates both basePrice and compareAtPrice.
     */
    loadInitialPrice() {
      // Try to get prices from selected Accu radio button
      var selectedAccu = document.querySelector('input[name^="Accu"]:checked');
      if (selectedAccu && selectedAccu.dataset.variantPrice) {
        var price = this.validateNumber(selectedAccu.dataset.variantPrice, 0, 100000000);
        if (price !== null) {
          this.basePrice = price / 100;

          // Compare-at from same input
          var compare = this.validateNumber(selectedAccu.dataset.variantCompareAtPrice, 0, 100000000);
          this.compareAtPrice = (compare !== null && compare > price) ? compare / 100 : 0;

          debugLog('Initial price from Accu selection:', this.basePrice, 'compare:', this.compareAtPrice);
          return;
        }
      }

      // Fallback to product base price element
      var basePriceElement = document.querySelector('[data-product-base-price]');
      if (basePriceElement) {
        var priceInCents = this.validateNumber(basePriceElement.dataset.productBasePrice, 0, 100000000);
        if (priceInCents !== null) {
          this.basePrice = priceInCents / 100;

          var compareCents = this.validateNumber(basePriceElement.dataset.productCompareAtPrice, 0, 100000000);
          this.compareAtPrice = (compareCents !== null && compareCents > priceInCents) ? compareCents / 100 : 0;

          debugLog('Initial price from base price element:', this.basePrice, 'compare:', this.compareAtPrice);
          return;
        }
      }

      debugLog('No initial price found');
      this.basePrice = 0;
      this.compareAtPrice = 0;
    }

    /**
     * Forceer zakelijk lease type wanneer prijs >= 5000
     */
    enforceZakelijkOnly() {
      this.leaseType = 'zakelijk';
      this.leaseTerm = '36';

      var zakelijkRadio = document.getElementById('lease_type_zakelijk');
      if (zakelijkRadio && zakelijkRadio.type === 'radio') {
        zakelijkRadio.checked = true;
      }

      var term36Radio = document.getElementById('lease_termijn_36mnd');
      if (term36Radio && term36Radio.type === 'radio') {
        term36Radio.checked = true;
      }

      var priveRadio = document.getElementById('lease_type_prive');
      var priveLabel = document.querySelector('label[for="lease_type_prive"]');
      if (priveRadio) priveRadio.style.display = 'none';
      if (priveLabel) priveLabel.style.display = 'none';

      var term48Radio = document.getElementById('lease_termijn_48mnd');
      var term48Label = document.querySelector('label[for="lease_termijn_48mnd"]');
      if (term48Radio) term48Radio.style.display = 'none';
      if (term48Label) term48Label.style.display = 'none';

      try {
        sessionStorage.setItem('leaseType', this.leaseType);
        sessionStorage.setItem('leaseTerm', this.leaseTerm);
      } catch (e) {
        debugLog('Error saving to sessionStorage:', e);
      }

      debugLog('Enforced zakelijk-only mode (price >= 5000)');
    }

    setupEventListeners() {
      var self = this;

      // Lease type changes (prive/zakelijk)
      var leaseTypeInputs = document.querySelectorAll('input[name="lease_type"]');
      leaseTypeInputs.forEach(function(input) {
        input.addEventListener('change', function(e) {
          var value = e.target.value;
          if (value !== 'prive' && value !== 'zakelijk') return;

          self.leaseType = value;

          if (self.leaseType === 'zakelijk') {
            self.leaseTerm = '36';
            var term36Radio = document.getElementById('lease_termijn_36mnd');
            if (term36Radio && term36Radio.type === 'radio') {
              term36Radio.checked = true;
            }
          } else {
            self.leaseTerm = '48';
            var term48Radio = document.getElementById('lease_termijn_48mnd');
            if (term48Radio && term48Radio.type === 'radio') {
              term48Radio.checked = true;
            }
          }

          self.updateLeasePrice();
        });
      });

      // Lease term changes (36/48 months)
      var leaseTermInputs = document.querySelectorAll('input[name="lease_term"]');
      leaseTermInputs.forEach(function(input) {
        input.addEventListener('change', function(e) {
          var value = e.target.value;
          if (value !== '36' && value !== '48') return;

          self.leaseTerm = value;
          self.updateLeasePrice();
        });
      });

      // Accu option changes - update BOTH basePrice AND compareAtPrice
      document.addEventListener('change', function(e) {
        if (!e.target.matches('input[name^="Accu"]')) return;

        var rawPrice = e.target.dataset.variantPrice;
        if (!rawPrice) return;

        var validatedPrice = self.validateNumber(rawPrice, 0, 100000000);
        if (validatedPrice === null) return;

        self.basePrice = validatedPrice / 100;

        // Update compare-at for this variant
        var rawCompare = e.target.dataset.variantCompareAtPrice;
        var validatedCompare = self.validateNumber(rawCompare, 0, 100000000);
        if (validatedCompare !== null && validatedCompare > validatedPrice) {
          self.compareAtPrice = validatedCompare / 100;
        } else {
          self.compareAtPrice = 0;
        }

        debugLog('Price updated from Accu selection:', self.basePrice, 'compare:', self.compareAtPrice);

        if (self.basePrice >= 5000) {
          self.enforceZakelijkOnly();
        }

        self.updateLeasePrice();
      });
    }

    findLeasePrice(leaseType, leaseTerm, totalPrice) {
      var price = (totalPrice !== undefined && totalPrice !== null) ? totalPrice : this.basePrice;
      var type = leaseType || this.leaseType;
      var term = leaseTerm || this.leaseTerm;

      if (type !== 'prive' && type !== 'zakelijk') return null;
      if (term !== '36' && term !== '48') return null;

      var validatedPrice = this.validateNumber(price, 0, 100000);
      if (validatedPrice === null) return null;

      var matchingEntry = null;
      for (var i = 0; i < this.leaseTableData.length; i++) {
        var entry = this.leaseTableData[i];
        if (entry.type === type && validatedPrice >= entry.van && validatedPrice <= entry.tot_en_met) {
          matchingEntry = entry;
          break;
        }
      }

      if (!matchingEntry) {
        debugLog('No matching lease price found for:', type, term, validatedPrice);
        return null;
      }

      var leasePrice = (term === '36') ? matchingEntry.lease_price_36 : matchingEntry.lease_price_48;

      return this.validateNumber(leasePrice, 0, 10000);
    }

    updateLeasePrice() {
      // Calculate prive price with current term
      this.privePrice = this.findLeasePrice('prive', this.leaseTerm);

      // Zakelijk always uses 36 months
      this.zakelijkPrice = this.findLeasePrice('zakelijk', '36');

      // Get the price for the selected lease type
      var monthlyPrice = (this.leaseType === 'prive') ? this.privePrice : this.zakelijkPrice;

      if (monthlyPrice === null) {
        this.displayError();
        return;
      }

      // Calculate compare-at lease price using the same lease type/term as the current price.
      // Only include it if the resulting lease price is actually higher than the current one.
      var compareLeasePrice = null;
      if (this.compareAtPrice > 0 && this.compareAtPrice > this.basePrice) {
        var candidate = this.findLeasePrice(this.leaseType, this.leaseTerm, this.compareAtPrice);
        if (candidate !== null && candidate > monthlyPrice) {
          compareLeasePrice = candidate;
        }
      }

      // Update main lease price display
      var leaseDisplayElement = document.getElementById('lease-price-display');
      if (leaseDisplayElement) {
        this.renderLeasePrice(leaseDisplayElement, monthlyPrice, compareLeasePrice);
        leaseDisplayElement.classList.remove('hidden');
        leaseDisplayElement.classList.remove('error');

        // Expose current lease price as dataset so other scripts (e.g. lease-button.js)
        // can read it without parsing text content.
        // BELANGRIJK: currentLeasePrice = NORMALE prijs. De 50%-actie is puur visueel;
        // het externe lease-formulier moet de echte (normale) maandprijs krijgen.
        leaseDisplayElement.dataset.currentLeasePrice = monthlyPrice.toFixed(2);
        leaseDisplayElement.dataset.promoLeasePrice = promoPriceFrom(monthlyPrice).toFixed(2);
        if (compareLeasePrice !== null) {
          leaseDisplayElement.dataset.compareLeasePrice = compareLeasePrice.toFixed(2);
        } else {
          delete leaseDisplayElement.dataset.compareLeasePrice;
        }
      }

      // Update total price display
      var totalPriceElement = document.getElementById('total-bike-price');
      if (totalPriceElement) {
        totalPriceElement.textContent = this.formatPrice(this.basePrice);
      }

      // Update product retail price display
      var retailPriceElement = document.querySelector('[data-product-retail-price]');
      if (retailPriceElement) {
        retailPriceElement.textContent = this.formatPrice(this.basePrice);
      }

      // Update mobile lease price display
      var mobileTextElement = document.getElementById('lease-price-mobile-text');
      if (mobileTextElement) {
        if (this.leaseType === 'prive') {
          mobileTextElement.textContent =
            'De eerste ' + PROMO_MONTHS + ' mnd \u20AC' + this.formatPriceNumber(promoPriceFrom(monthlyPrice)) +
            ' p/m, daarna \u20AC' + this.formatPriceNumber(monthlyPrice);
        } else {
          mobileTextElement.textContent = 'Prijs per maand \u20AC' + this.formatPriceNumber(monthlyPrice);
        }
      }

      this.updateComparisonPrices();
      this.updatePageTitle(monthlyPrice);

      debugLog('Lease price updated:', monthlyPrice, 'compare:', compareLeasePrice, '(type:', this.leaseType, ', term:', this.leaseTerm, ')');
    }

    updatePageTitle(price) {
      var productTitleElement = document.querySelector('[data-product-title]');
      if (!productTitleElement) return;

      var title = productTitleElement.getAttribute('data-product-title');
      if (!title) return;

      var formattedPrice;
      if (this.leaseType === 'prive') {
        formattedPrice = this.formatPriceNumber(promoPriceFrom(price));
        document.title = title + " - De eerste " + PROMO_MONTHS + " maanden \u20AC" + formattedPrice + " per maand";
      } else {
        formattedPrice = this.formatPriceNumber(price);
        document.title = title + " - Lease 'm voor \u20AC" + formattedPrice + " per maand";
      }
    }

    /**
     * Veilig renderen van lease prijs zonder innerHTML.
     * Actie-weergave: eerste 3 maanden 50% korting, daarna normale prijs.
     * Structuur:
     *   <div class="lease-price-amount">
     *     <p class="lease-price-promo">
     *       <span class="lease-price-promo__lead">De eerste 3 maanden voor </span>
     *       <span class="lease-price-current lease-price-promo__amount">€31,04</span>
     *       <span class="lease-price-promo__unit"> per maand</span>
     *       <span class="lease-price-promo__sep">, daarna </span>
     *       <s class="lease-price-compare">€70,84</s>            <-- alleen bij compare-at
     *       <span class="lease-price-promo__after-amount">€62,07</span>
     *       <span class="lease-price-promo__unit"> per maand</span>
     *     </p>
     *   </div>
     *
     * @param {number} price        de normale maandprijs (wat je daarna betaalt)
     * @param {number|null} comparueprice  optionele doorgestreepte 'van'-prijs
     */
    /**
     * Veilig renderen van het lease-prijsblok (zonder innerHTML).
     * Layout zoals het actie-prijsblok:
     *   [ WK-actie: 50% korting ]            <- badge
     *   <s>€97,95</s>  €48,98                <- doorgestreepte normale prijs + grote actieprijs
     *   per maand
     *   Eerste 3 maanden, daarna €97,95 per maand
     *
     * @param {number} price        de normale maandprijs
     * @param {number|null} comparePrice  niet meer gebruikt in deze layout
     */
    renderLeasePrice(element, price, comparePrice) {
      // Clear existing content
      while (element.firstChild) {
        element.removeChild(element.firstChild);
      }

      var normalStr = '\u20AC' + this.formatPriceNumber(price);

      // Zakelijk: GEEN actie — gewoon de normale prijs tonen.
      if (this.leaseType === 'zakelijk') {
        var zblock = document.createElement('div');
        zblock.className = 'lease-price-amount lease-promo-block';

        var zmain = document.createElement('div');
        zmain.className = 'lease-promo-main';
        var znow = document.createElement('span');
        znow.className = 'lease-promo-now';
        znow.textContent = normalStr;
        zmain.appendChild(znow);
        zblock.appendChild(zmain);

        var zsub = document.createElement('div');
        zsub.className = 'lease-promo-sub';
        zsub.textContent = 'per maand';
        zblock.appendChild(zsub);

        element.appendChild(zblock);
        return;
      }

      // Privé: actie-blok (badge + gehalveerde prijs + uitleg).
      var promoPrice = promoPriceFrom(price);

      var block = document.createElement('div');
      block.className = 'lease-price-amount lease-promo-block';

      // Badge
      var badge = document.createElement('span');
      badge.className = 'lease-promo-badge';
      badge.textContent = PROMO_BADGE_LABEL;
      block.appendChild(badge);

      // Hoofdregel: doorgestreepte normale prijs + grote actieprijs
      var main = document.createElement('div');
      main.className = 'lease-promo-main';

      var was = document.createElement('s');
      was.className = 'lease-promo-was';
      was.textContent = normalStr;
      main.appendChild(was);

      var now = document.createElement('span');
      now.className = 'lease-promo-now';
      now.textContent = '\u20AC' + this.formatPriceNumber(promoPrice);
      main.appendChild(now);

      block.appendChild(main);

      // "per maand"
      var sub = document.createElement('div');
      sub.className = 'lease-promo-sub';
      sub.textContent = 'per maand';
      block.appendChild(sub);

      // "Eerste 3 maanden, daarna €97,95 per maand"
      var after = document.createElement('div');
      after.className = 'lease-promo-after';
      after.appendChild(document.createTextNode('Eerste ' + PROMO_MONTHS + ' maanden, daarna '));
      var afterStrong = document.createElement('strong');
      afterStrong.textContent = normalStr;
      after.appendChild(afterStrong);
      after.appendChild(document.createTextNode(' per maand'));
      block.appendChild(after);

      element.appendChild(block);
    }

    updateComparisonPrices() {
      var privePriceElement = document.getElementById('prive_prijs');
      if (privePriceElement && this.privePrice !== null) {
        privePriceElement.textContent = this.formatPriceNumber(this.privePrice);
      }

      var zakelijkPriceElement = document.getElementById('zakelijk_prijs');
      if (zakelijkPriceElement && this.zakelijkPrice !== null) {
        zakelijkPriceElement.textContent = this.formatPriceNumber(this.zakelijkPrice);
      }
    }

    formatPriceNumber(price) {
      var validated = this.validateNumber(price, 0, 100000);
      if (validated === null) return '0,00';
      return validated.toFixed(2).replace('.', ',');
    }

    formatPrice(price) {
      return '€' + this.formatPriceNumber(price);
    }

    displayError() {
      var leaseDisplayElement = document.getElementById('lease-price-display');
      if (leaseDisplayElement) {
        leaseDisplayElement.textContent = 'Lease prijs niet beschikbaar';
        leaseDisplayElement.classList.add('error');
        delete leaseDisplayElement.dataset.currentLeasePrice;
        delete leaseDisplayElement.dataset.compareLeasePrice;
      }
      debugLog('Error: Could not calculate lease price');
    }
  }

  if (!customElements.get('lease-calculator')) {
    customElements.define('lease-calculator', LeaseCalculator);
  }
})();